import * as React from 'react';
import { DotLottiePlayer } from '@dotlottie/react-player';
import { useDelayedHover } from '@mentimeter/react-hooks';
import { animated, type SpringValues, useTransition } from 'react-spring';
import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Heading } from '@mentimeter/ragnar-ui/typography';
import { Text } from '@mentimeter/ragnar-ui/text';

const ANIMATION_URL =
  'https://static.mentimeter.com/static/motion/facilitator-participation_animation.lottie';

function TooltipContent() {
  return (
    <>
      <Box width="100%" height="189px">
        <Box position="absolute" top={0} left={0} height="100%" width="100%">
          <DotLottiePlayer src={ANIMATION_URL} autoplay />
        </Box>
      </Box>
      <Box width="100%" p="space8">
        <Heading fontSize="125" color="textWeak" fontWeight="regular">
          Make your voice heard, too
        </Heading>
        <Text fontSize="100" color="textWeak">
          Respond to questions while sharing the presentation
        </Text>
      </Box>
    </>
  );
}

export const Tooltip = ({
  referenceId,
  children,
}: {
  referenceId: string;
  children: React.ReactNode;
}) => {
  const isHovered = useDelayedHover(0, referenceId);
  const { theme } = useRagnar();
  const number = theme.durations[1]! * 1000;
  const transition = useTransition(isHovered, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: number },
  });
  return transition((styles, show) => {
    if (!show) return null;
    return (
      <Box
        as={animated.div}
        bg="surfaceOverlayTransparent"
        extend={({ theme }) => ({
          backdropFilter: theme.kosmosBlur.xl,
          WebkitBackdropFilter: theme.kosmosBlur.xl,
        })}
        style={styles as SpringValues}
        borderRadius={2}
        width="304px"
        height="100%"
        overflow="auto"
        flex="1 1 auto"
        my={2}
      >
        {children}
      </Box>
    );
  });
};
export const ParticipationTooltip = ({
  referenceId,
}: {
  referenceId: string;
}) => {
  return (
    <Tooltip referenceId={referenceId}>
      <TooltipContent />
    </Tooltip>
  );
};
